/**
 * zod-схемы входящих данных: REST-тела и payload'ы WebSocket-событий.
 * Сервер валидирует ими всё, что приходит от клиента; клиент — формы.
 */
import { z } from 'zod';
import { AVATAR_IDS } from './avatars';
import { NIGHT_ACTIONS, ROLE_IDS } from './roles';
import { GAME_KINDS } from './kinds';
import { MAX_PLAYERS, MIN_PLAYERS } from './settings';

const seat = z.number().int().min(1).max(MAX_PLAYERS);

export const emailSchema = z.string().trim().toLowerCase().email('Некорректный email').max(254);
export const loginSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9_]{3,24}$/, 'Логин: 3–24 символа, латиница, цифры и _');
export const passwordSchema = z.string().min(8, 'Минимум 8 символов').max(128, 'Максимум 128 символов');
export const nicknameSchema = z
  .string()
  .trim()
  .min(2, 'Минимум 2 символа')
  .max(24, 'Максимум 24 символа')
  .regex(/^[\p{L}\p{N} _.\-]+$/u, 'Только буквы, цифры, пробел и _ . -');

// ---------------- REST ----------------

const pdConsentSchema = z.literal(true, { errorMap: () => ({ message: 'Нужно согласие на обработку персональных данных' }) });

export const registerBody = z.object({
  email: emailSchema,
  login: loginSchema,
  password: passwordSchema,
  nickname: nicknameSchema.optional(),
  pdConsent: pdConsentSchema,
});

export const consentBody = z.object({ pdConsent: pdConsentSchema });

export const loginBody = z.object({
  /** Email или логин. */
  identifier: z.string().trim().toLowerCase().min(1).max(254),
  password: z.string().min(1).max(128),
});

export const forgotBody = z.object({ email: emailSchema });
export const resetBody = z.object({ token: z.string().min(20).max(200), password: passwordSchema });
export const changePasswordBody = z.object({ currentPassword: z.string().min(1).max(128), newPassword: passwordSchema });
/** Ник в Telegram: принимает «@name», «t.me/name» или «name»; пустая строка — убрать. */
export const telegramSchema = z
  .string()
  .trim()
  .transform((v) => v.replace(/^(https?:\/\/)?(t|telegram)\.me\//i, '').replace(/^@/, ''))
  .pipe(z.union([z.literal(''), z.string().regex(/^[A-Za-z][A-Za-z0-9_]{3,31}$/, 'Ник в Telegram: 4–32 символа, латиница, цифры и _')]))
  .transform((v) => v || null);

export const deleteMeBody = z.object({ password: z.string().min(1, 'Введите пароль').max(128) });
export const deleteUserBody = z.object({ reason: z.string().trim().max(500).optional() });

export const updateMeBody = z.object({
  nickname: nicknameSchema.optional(),
  avatarId: z.enum(AVATAR_IDS).nullable().optional(),
  telegram: telegramSchema.nullable().optional(),
});

export const timersSchema = z.object({
  roleRevealSec: z.number().int().min(3).max(600),
  nightSec: z.number().int().min(3).max(600),
  morningSec: z.number().int().min(3).max(600),
  speechSec: z.number().int().min(3).max(600),
  tieSpeechSec: z.number().int().min(3).max(600),
  votingSec: z.number().int().min(3).max(600),
  lastWordSec: z.number().int().min(3).max(600),
  /** Старые комнаты сохранены без этого поля. */
  discussionSec: z.number().int().min(0).max(600).default(60),
});

const roleCount = z.number().int().min(0).max(MAX_PLAYERS).optional();
export const gameSettingsSchema = z.object({
  roles: z
    .object({ MAFIA: roleCount, DON: roleCount, SHERIFF: roleCount, DOCTOR: roleCount, LOVER: roleCount, MANIAC: roleCount })
    .strict(),
  timers: timersSchema,
  doctorSelfHeals: z.number().int().min(0).max(10),
  revealRoleOnDeath: z.boolean(),
  nightVictimLastWord: z.boolean(),
  nightEndsEarly: z.boolean(),
  openVoting: z.boolean(),
  pace: z.enum(['auto', 'manual']).default('auto'),
  skipVoteOnce: z.boolean().default(true),
});

export const createRoomBody = z
  .object({
    name: z.string().trim().min(2, 'Минимум 2 символа').max(48),
    isPrivate: z.boolean(),
    password: z.string().min(1).max(64).optional(),
    maxPlayers: z.number().int().min(MIN_PLAYERS).max(MAX_PLAYERS),
    settings: gameSettingsSchema.optional(),
    /** Турнирный стол — только для администраторов (проверяет сервер). */
    kind: z.enum(GAME_KINDS).default('AMATEUR'),
  })
  .refine((b) => !b.password || b.isPrivate, { message: 'Пароль только для приватной комнаты', path: ['password'] });

export const banBody = z.object({
  reason: z.string().trim().min(1).max(500),
  /** ISO-дата окончания; не задана — бессрочно. */
  until: z.string().datetime().optional(),
});
export const setRoleBody = z.object({ role: z.enum(['PLAYER', 'ADMIN']) });

const statCount = z.number({ invalid_type_error: 'Число' }).int('Целое число').min(0, 'Не меньше 0').max(100_000);
export const roleStatInput = z
  .object({
    roleId: z.enum(ROLE_IDS),
    played: statCount,
    won: statCount,
    survived: statCount,
    points: z.number({ invalid_type_error: 'Число' }).int('Целое число').min(-1_000_000).max(1_000_000),
  })
  .superRefine((r, ctx) => {
    if (r.won > r.played) ctx.addIssue({ code: 'custom', path: ['won'], message: 'Побед больше, чем игр' });
    if (r.survived > r.played) ctx.addIssue({ code: 'custom', path: ['survived'], message: 'Выживаний больше, чем игр' });
  });
/** Полная замена статистики игрока по ролям; роли без строки — нули. */
export const editStatsBody = z.object({
  /** Какую статистику правим — любительскую или турнирную. */
  kind: z.enum(GAME_KINDS),
  byRole: z
    .array(roleStatInput)
    .max(ROLE_IDS.length)
    .refine((rows) => new Set(rows.map((r) => r.roleId)).size === rows.length, 'Роль указана дважды'),
  reason: z.string().trim().max(500).optional(),
});
/** kind не указан — обнуляется статистика всех видов игр. */
export const resetStatsBody = z.object({ kind: z.enum(GAME_KINDS).optional(), reason: z.string().trim().max(500).optional() });
export const ratingQuery = z.object({ kind: z.enum(GAME_KINDS).default('AMATEUR') });

// ---------------- WebSocket ----------------

export const wsSchemas = {
  'room:join': z.object({ roomId: z.string().uuid(), password: z.string().max(64).optional() }),
  'room:ready': z.object({ ready: z.boolean() }),
  'room:updateSettings': z.object({
    maxPlayers: z.number().int().min(MIN_PLAYERS).max(MAX_PLAYERS).optional(),
    settings: gameSettingsSchema.optional(),
    /** Хост ведёт игру без роли (только администраторы сайта). */
    moderated: z.boolean().optional(),
  }),
  'room:kick': z.object({ userId: z.string().uuid() }),
  /** target = 0 у MAFIA_KILL — «не убивать». */
  'night:action': z.object({ action: z.enum(NIGHT_ACTIONS), target: z.number().int().min(0).max(MAX_PLAYERS) }),
  'day:nominate': z.object({ target: seat }),
  /** target = 0 — «пропустить голосование». */
  'vote:cast': z.object({ target: z.number().int().min(0).max(MAX_PLAYERS) }),
  'game:kick': z.object({ seat }),
  /** token — phaseToken из снапшота ведущего. */
  'game:advance': z.object({ token: z.number().int().min(0) }),
} as const;
